import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { importanceMeta } from "@/lib/taskUi";
import { Users, Building2, UserCheck, ShieldAlert, ArrowRight, Star, Clock, AlertTriangle, CalendarDays, CheckCircle2, Target } from "lucide-react";
import ReferralDashboard from "@/pages/referrals/ReferralDashboard";
import { GoalProgressGrid } from "@/pages/recruiting/dashboard/GoalProgressGrid";
import AgentHome from "@/pages/agent/AgentHome";

function StatCard({ icon: Icon, label, value, onClick, tone = "navy", testId }) {
  const tones = { navy: "text-[#006090] bg-[#006090]/10", teal: "text-[#0093D6] bg-[#0093D6]/10",
    amber: "text-amber-600 bg-amber-100", indigo: "text-indigo-600 bg-indigo-100", rose: "text-rose-600 bg-rose-100" };
  return (
    <button onClick={onClick} data-testid={testId}
      className="text-left rounded-lg border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all group">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
        {onClick && <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500" />}
      </div>
      <p className="mt-4 text-3xl font-display font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </button>
  );
}

export default function Home() {
  const { user, currentAccount, isAdmin, isSuper, canRecruiting, canTasks, isReferralPartner, roles, isGroupAdmin, isManager, isOutside, viewAs } = useAuth();
  const isAgentOnly = (roles.has("agent") || roles.has("agent_staff")) && !canRecruiting && !isAdmin && !isGroupAdmin && !isManager && !isSuper;
  const navigate = useNavigate();
  const [stats, setStats] = useState({ people: 0, orgs: 0, candidates: 0 });
  const [top10, setTop10] = useState(null);
  const [summary, setSummary] = useState(null);
  const [goals, setGoals] = useState(null);

  useEffect(() => {
    // Reset on every identity/View As change so a previous session's numbers can never linger on screen.
    setStats({ people: 0, orgs: 0, candidates: 0 }); setTop10(null); setSummary(null); setGoals(null);
    if (isReferralPartner || isAgentOnly || isOutside) return;
    (async () => {
      try {
        const [counts, orgs] = await Promise.all([api.get("/persons/counts"), api.get("/organizations")]);
        setStats({ people: counts.data.all, candidates: counts.data.by_classification?.candidate || 0,
          orgs: orgs.data.organizations?.length || 0 });
      } catch {}
    })();
    if (canTasks && !isReferralPartner) {
      api.get("/tasks/top10").then((r) => setTop10(r.data)).catch(() => {});
      api.get("/tasks/summary").then((r) => setSummary(r.data)).catch(() => {});
    }
    // Personalized Goal Progress — reuses the same goal calculations/permissions as the Recruiting
    // Dashboard. Server scopes to the logged-in user's role (recruiter -> own, manager -> team).
    if (canRecruiting && !isReferralPartner) {
      api.get("/recruiting/goals/progress", { params: { year: new Date().getFullYear() } })
        .then((r) => setGoals(r.data)).catch(() => setGoals(null));
    }
  }, [currentAccount, canTasks, canRecruiting, isReferralPartner, isAgentOnly, isOutside, viewAs?.role, viewAs?.person_id]);

  const complete = async (id) => { try { await api.patch(`/tasks/${id}`, { status: "completed" }); api.get("/tasks/top10").then((r) => setTop10(r.data)); api.get("/tasks/summary").then((r) => setSummary(r.data)); } catch {} };

  if (isReferralPartner || isOutside) return <ReferralDashboard />;
  if (isAgentOnly) return <AgentHome />;

  return (
    <div>
      <PageHeader title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`}
        description={currentAccount ? `You're working in ${currentAccount.account_name}.` : "Select a client account to begin."} />

      {user?.mfa_required && !user?.mfa_enabled && (
        <div className="mb-6 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="mfa-banner">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800 flex-1">Your role requires two-factor authentication. Secure your account now.</p>
          <button onClick={() => navigate("/security?setup=1")} className="text-sm font-semibold text-amber-800 hover:underline">Set up MFA</button>
        </div>
      )}

      {canTasks && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="home-task-widgets">
          <StatCard icon={Clock} label="Due Today" value={summary.due_today} tone="amber" testId="home-due-today" onClick={() => navigate("/tasks")} />
          <StatCard icon={AlertTriangle} label="Overdue" value={summary.overdue} tone="rose" testId="home-overdue" onClick={() => navigate("/tasks")} />
          <StatCard icon={CalendarDays} label="Upcoming (7d)" value={summary.upcoming_week} tone="teal" testId="home-upcoming" onClick={() => navigate("/calendar")} />
          <StatCard icon={CheckCircle2} label="Done This Week" value={summary.completed_week} tone="indigo" testId="home-done-week" onClick={() => navigate("/tasks")} />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {canTasks && (
          <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="home-daily-top10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                <h3 className="font-display font-semibold text-slate-900">Daily Top 10</h3>
                {top10 && <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2">{top10.count}/10</span>}</div>
              <button onClick={() => navigate("/tasks")} className="text-sm text-brand hover:underline">Open Tasks</button>
            </div>
            {top10 && top10.items.length ? (
              <div className="divide-y divide-slate-100">
                {top10.items.map((t, i) => {
                  const im = importanceMeta(t.importance);
                  return (
                    <div key={t.id} className="flex items-center gap-3 py-2.5" data-testid={`home-top10-${t.id}`}>
                      <span className="text-xs font-bold text-slate-300 w-4">{i + 1}</span>
                      <button onClick={() => complete(t.id)} className="w-5 h-5 rounded-full border-2 border-slate-300 hover:border-emerald-500 shrink-0" data-testid={`home-top10-complete-${t.id}`} />
                      <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold ${im.badge}`}><im.icon className="w-3 h-3 mr-0.5" />{im.label}</span>
                      <button onClick={() => navigate(`/tasks/detail/${t.id}`)} className="text-sm text-slate-700 flex-1 text-left truncate hover:text-brand">{t.title}</button>
                      {t.is_overdue && <span className="text-xs text-rose-600">Overdue</span>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-slate-400 mb-2">No Top 10 tasks selected for today.</p>
                <button onClick={() => navigate("/tasks")} className="text-sm font-semibold text-brand hover:underline">Build your Daily Top 10 →</button>
              </div>
            )}
          </div>
        )}

        {!isOutside && <div className="space-y-3">
          <StatCard icon={Users} label="People" value={stats.people} tone="navy" testId="stat-people" onClick={() => navigate("/people")} />
          <StatCard icon={UserCheck} label="Candidates" value={stats.candidates} tone="teal" testId="stat-candidates" onClick={() => navigate("/people/candidate")} />
          <StatCard icon={Building2} label="Organizations" value={stats.orgs} tone="indigo" testId="stat-orgs" onClick={() => navigate(isAdmin ? "/admin/organizations" : "/people")} />
        </div>}
      </div>

      {canRecruiting && goals?.has_goal && (
        <div className="mt-8" data-testid="home-goal-progress">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
              <Target className="w-5 h-5 text-brand" /> Goal Progress
              {goals?.year && <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">{goals.year}</span>}
            </h3>
            <button onClick={() => navigate("/recruiting")} className="text-sm text-brand hover:underline" data-testid="home-goal-open-dashboard">Open Recruiting Dashboard</button>
          </div>
          <GoalProgressGrid progress={goals} />
        </div>
      )}

      {isSuper && (
        <div className="mt-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-display font-semibold text-slate-900">Platform</h3>
          <button onClick={() => navigate("/super-admin")} className="mt-2 text-sm text-brand hover:underline">Manage Client Accounts in Super Admin →</button>
        </div>
      )}
    </div>
  );
}
